import { task, waitForProperty } from 'ember-concurrency';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { waitFor } from '@ember/test-waiters';
import { dedupeTracked } from 'tracked-toolbox';
import DataTypes, { DECIMAL_LENGTH } from 'eflex/constants/component-data-types';
import { isPresent } from '@ember/utils';
import { incrementPriority, decrementPriority, getNextPriority } from 'eflex/util/component-priority-helpers';
import BarcodeBomSource from 'eflex/components/bom-sources/barcode-bom-source';
import GepicsBomSource from 'eflex/components/bom-sources/gepics-bom-source';
import OracleDellBoomiBomSource from 'eflex/components/bom-sources/oracle-dell-boomi-bom-source';
import RestBomSource from 'eflex/components/bom-sources/rest-bom-source';
import RestPullBomSource from 'eflex/components/bom-sources/rest-pull-bom-source';
import { PERMISSION_NAMES } from 'eflex/constants/user-permissions';
import ConfigurationHistoryTypes from 'eflex/constants/configuration-history-types';

export default class BomSourceController extends Controller {
  @service componentRepo;
  @service bomSourceRepo;
  @service store;
  @service notifier;
  @service validationErrorNotifier;
  @service router;
  @service currentUser;
  @service systemConfig;

  @tracked bomSource;
  @tracked activeTab = 'components';
  @tracked showOptionsPanel = false;
  @tracked showConfigurationHistoryNotesModal = false;
  @dedupeTracked selectedConfig;

  variableDataTypes = DataTypes;
  queryParams = ['selectedConfigId'];
  permission = PERMISSION_NAMES.bomSources;
  bomConfigurationHistoriesType = ConfigurationHistoryTypes.BOM_SOURCE;

  get hasPermission() {
    return this.currentUser.user.hasPermission(this.permission);
  }

  get selectedConfigId() {
    return this.selectedConfig?.id;
  }

  set selectedConfigId(id) {
    if (id == null || this.selectedConfig?.id === id) {
      return;
    }

    const selectedConfig = this.store.peekRecord('component', id);

    if (selectedConfig) {
      this.activeTab = selectedConfig.isComplex ? 'complex-components' : 'components';
      this.onOpenOptionsPanel(selectedConfig);
    } else if (this.store.peekRecord('bom-variable', id)) {
      this.activeTab = 'variables';
    }
  }

  get configComponent() {
    switch (this.bomSource.constructor.modelName) {
      case 'barcode-bom-source': {
        return BarcodeBomSource;
      }
      case 'gepics-bom-source': {
        return GepicsBomSource;
      }
      case 'oracle-dell-boomi-bom-source': {
        return OracleDellBoomiBomSource;
      }
      case 'rest-bom-source': {
        return RestBomSource;
      }
      case 'rest-pull-bom-source': {
        return RestPullBomSource;
      }
      default: {
        return null;
      }
    }
  }

  get selectedTasksCount() {
    return this.selectedConfig?.tasks?.length;
  }

  get hasSelectedTasks() {
    return this.selectedTasksCount > 0;
  }

  get allNonDeletedComponents() {
    return this.bomSource?.components
      .filter(component => !component.isDeleted && !component.isAlwaysRun);
  }

  get nonDeletedComponentsWithoutComplex() {
    return this.bomSource?.components
      .filter(component => !component.isDeleted && !component.isAlwaysRun && !component.isComplex);
  }

  get nonDeletedComplexComponents() {
    return this.bomSource?.components
      .filter(component => !component.isDeleted && component.isComplex);
  }

  get nonDeletedVariables() {
    return this.bomSource.variables.filter(item => !item.isDeleted);
  }

  get isDirty() {
    return this.bomSource?.isDirty || this.bomSource?.components.some(item => item.isDirty);
  }

  get isInvalid() {
    return this.bomSource?.isSelfOrChildInvalid ?? false;
  }

  // TODO: It seem like we're redo-ing this logic in a lot of places
  save = task(waitFor(async () => {
    if (this.isInvalid) {
      this.validationErrorNotifier.sendErrors(
        [this.bomSource].concat(this.allNonDeletedComponents),
      );
      return;
    }

    await this.bomSourceRepo.save.perform(this.bomSource);

    if (this.systemConfig.jem?.configurationHistory && this.bomSource.stations.length > 0) {
      this.showConfigurationHistoryNotesModal = true;
      await waitForProperty(this, 'showConfigurationHistoryNotesModal', false);
    }

  }));

  @action
  rollback(fromTransitionModal = false) {
    if (this.bomSource.isDeleted) {
      return;
    }

    this.bomSource.rollbackAttributes();

    this.bomSource.areas.forEach(area => { area.rollbackAttributes(); });

    if (this.bomSource.isDeleted) {
      this.bomSource = null;

      if (!fromTransitionModal) {
        this.router.transitionTo('bomSources.index');
      }
    }
  }

  @action
  incrementPriority(config) {
    incrementPriority(config, config.peers);
  }

  @action
  decrementPriority(config) {
    decrementPriority(config, config.peers);
  }

  @action
  addComponent(isComplex = false) {
    this.store.createRecord('component', {
      bomSource: this.bomSource,
      priority: isComplex
        ? getNextPriority(this.nonDeletedComplexComponents) : getNextPriority(this.nonDeletedComponentsWithoutComplex),
      isComplex,
    });
  }

  @action
  addVariable() {
    this.store.createRecord('bom-variable', {
      bomSource: this.bomSource,
      priority: getNextPriority(this.nonDeletedVariables),
      length: DECIMAL_LENGTH,
    });
  }

  @action
  deleteConfig(config) {
    const errors = this.componentRepo.delete(config, this.nonDeletedComponentsWithoutComplex);

    if (isPresent(errors)) {
      errors?.forEach(captionParams => {
        this.notifier.sendError('components.deletionWarning', captionParams);
      });
      return;
    }

    this.closeOptionsPanel();
  }

  @action
  onOpenOptionsPanel(component) {
    Object.assign(this, {
      selectedConfig: component,
      showOptionsPanel: true,
    });
  }

  @action
  closeOptionsPanel() {
    Object.assign(this, {
      selectedConfig: null,
      showOptionsPanel: false,
    });
  }

  @action
  tabBecameActive(tab) {
    this.closeOptionsPanel();
    this.activeTab = tab;
  }
}
